import { useEffect, useState } from 'react';
import { collection, doc, getDoc, onSnapshot, query, where, type Query } from 'firebase/firestore';
import { COLLECTIONS, type BrandDoc, type BrandTheme, type CampaignDoc } from '@brands/shared';
import { useAuth } from '../auth/AuthContext';
import { firebase } from '../lib/firebase';
import type { Loadable } from './campaign';

export type Brand = BrandDoc & { id: string };
export type CampaignRow = CampaignDoc & { id: string };

function useList<T>(build: () => Query | null, deps: unknown[]): Loadable<T[]> {
  const [state, setState] = useState<Loadable<T[]>>({ status: 'loading' });
  useEffect(() => {
    const q = build();
    if (!q) return;
    return onSnapshot(
      q,
      (snap) =>
        setState({
          status: 'ready',
          data: snap.docs.map((d) => ({ ...(d.data() as T), id: d.id })),
        }),
      () => setState({ status: 'error', message: 'No pudimos cargar la información.' }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- las dependencias se pasan explícitas
  }, deps);
  return state;
}

/** Marcas visibles: todas para el admin; las propias para un cliente (lo exigen las reglas). */
export function useBrands(): Loadable<Brand[]> {
  const { session } = useAuth();
  return useList<Brand>(() => {
    if (!session) return null;
    const base = collection(firebase().db, COLLECTIONS.brands);
    return session.isStaff
      ? base
      : query(base, where('memberEmails', 'array-contains', session.email));
  }, [session]);
}

export function useBrand(id: string | undefined): Loadable<Brand> {
  const [state, setState] = useState<Loadable<Brand>>({ status: 'loading' });
  useEffect(() => {
    if (!id) return;
    return onSnapshot(
      doc(firebase().db, COLLECTIONS.brands, id),
      (snap) =>
        setState(
          snap.exists()
            ? { status: 'ready', data: { ...(snap.data() as BrandDoc), id } }
            : { status: 'missing' },
        ),
      () => setState({ status: 'error', message: 'No pudimos cargar la marca.' }),
    );
  }, [id]);
  return state;
}

/** Marca por su subdominio (slug). Un cliente solo la encuentra si pertenece a ella. */
export function useBrandBySlug(slug: string | null): Loadable<Brand | null> {
  const { session } = useAuth();
  const list = useList<Brand>(() => {
    if (!session || !slug) return null;
    const base = collection(firebase().db, COLLECTIONS.brands);
    return session.isStaff
      ? query(base, where('slug', '==', slug))
      : query(
          base,
          where('slug', '==', slug),
          where('memberEmails', 'array-contains', session.email),
        );
  }, [session, slug]);
  return list.status === 'ready' ? { status: 'ready', data: list.data[0] ?? null } : list;
}

/** Campañas de una marca: todas para el admin; publicadas y habilitadas para un cliente. */
export function useBrandCampaigns(brandId: string | null | undefined): Loadable<CampaignRow[]> {
  const { session } = useAuth();
  return useList<CampaignRow>(() => {
    if (!session || !brandId) return null;
    const base = collection(firebase().db, COLLECTIONS.campaigns);
    return session.isStaff
      ? query(base, where('brandId', '==', brandId))
      : query(
          base,
          where('brandId', '==', brandId),
          where('memberEmails', 'array-contains', session.email),
          where('status', '==', 'published'),
        );
  }, [session, brandId]);
}

/** Campañas publicadas a las que tiene acceso el cliente (todas las marcas). */
export function useMyCampaigns(): Loadable<CampaignRow[]> {
  const { session } = useAuth();
  return useList<CampaignRow>(() => {
    if (!session) return null;
    const base = collection(firebase().db, COLLECTIONS.campaigns);
    return session.isStaff
      ? base
      : query(
          base,
          where('memberEmails', 'array-contains', session.email),
          where('status', '==', 'published'),
        );
  }, [session]);
}

/** Tema público del portal (nombre y colores) para el login; no requiere sesión. */
export function useBrandTheme(slug: string | null): BrandTheme | null {
  const [theme, setTheme] = useState<BrandTheme | null>(null);
  useEffect(() => {
    if (!slug) return;
    let alive = true;
    getDoc(doc(firebase().db, COLLECTIONS.brandThemes, slug)).then(
      (snap) => alive && setTheme(snap.exists() ? (snap.data() as BrandTheme) : null),
      () => undefined,
    );
    return () => {
      alive = false;
    };
  }, [slug]);
  return theme;
}
